/**
 * ชนิดข้อมูลของเคส NCR (Non-Conformance Report, D-108 ข้อ 2 และ 3) — เขียนตามที่
 * `apps/api/src/cap_erp_api/modules/quality/router.py`/`schemas.py`/`models.py`
 * serve จริง **ไม่ใช่ตาม `docs/API_CONTRACT.md`** (WO นี้ระบุตรง ๆ ว่าเอกสารนั้น
 * เคยพูดถึง route ที่ไม่เคยถูก serve มาสัปดาห์หนึ่งแล้ว)
 *
 * ห้าเส้นทางของ D-108: `GET`/`POST /v1/ncr-cases`, `GET` หนึ่งใบ, และสองการเปลี่ยน
 * สถานะ `POST .../{ncr_id}/investigate` กับ `POST .../{ncr_id}/close`
 *
 * สี่ข้อที่ต้องอ่านออกจากชนิดข้อมูลนี้โดยไม่ต้องเปิดเอกสาร:
 *
 * 1. **`NcrCase` ไม่มี `quality_status` ของล็อตอยู่เลย** — เคสนี้เป็นเรื่องของ *คดี*
 *    (พบอะไร สอบสวนอะไร ตัดสินอะไร) ส่วนสถานะของล็อตเป็นเรื่องของล็อต การอ่าน
 *    สถานะล็อตปัจจุบันไม่ใช่หน้าที่ของหน้าจอนี้และไม่ใช่หน้าที่ของ endpoint กลุ่มนี้
 * 2. **`reference_doc_type`/`reference_doc_id` เป็น typed soft reference ไม่มี
 *    foreign key จริง** (D-034) — ห้ามเดาว่า id นี้ resolve ไปเป็นอะไรได้ นอกจากพิมพ์
 *    ชนิดและ id ตรง ๆ ตามที่มันเป็น
 * 3. **`disposition`, `decided_by_employee_id`, `decided_at` เป็น `null` ทั้งสาม
 *    จนกว่าจะปิดเคส แล้วเป็นค่าทั้งสามพร้อมกันเสมอ** — ฐานข้อมูลฝั่งเซิร์ฟเวอร์บังคับ
 *    ไว้เป็นคู่ (`ck_ncr_case_disposition_record_is_whole`)
 * 4. **`DISPOSITION_LOT_STATUS` คือสิ่งที่ปิดเคสด้วยการตัดสินใจหนึ่งจะทำกับล็อต**
 *    (D-108 ข้อ 2, `modules/quality/models.py`) — คัดลอกมาจากตารางนั้นตรง ๆ
 *    เพื่อให้หน้าจอบอกผลลัพธ์ **ก่อน** กดยืนยัน ไม่ใช่แหล่งความจริงที่สอง: เซิร์ฟเวอร์
 *    เป็นผู้เขียนสถานะจริงเสมอ ตารางนี้มีไว้ทำนายผลให้ผู้ใช้เห็นล่วงหน้าเท่านั้น
 */

/**
 * รหัสโมดูลของหน้าจอนี้ใน RBAC matrix — **`INVENTORY` เดิม ไม่ใช่โมดูลใหม่ชื่อ
 * `QUALITY`** เหตุผลเดียวกับ `lib/quality/types.ts`: การรายงาน/สอบสวน/ปิดเคส NCR
 * เป็นสิทธิ์ชุดเดียวกับที่คนคลังและคนรับของถืออยู่แล้ว
 *
 * ประกาศซ้ำแทนการ import ข้ามโดเมนด้วยเหตุผลเดียวกับทุกโดเมนอื่นในโปรเจกต์นี้
 */
export const INVENTORY_MODULE = 'INVENTORY';

/* ------------------------------------------------------- ข้อจำกัดความยาว -- */

/** `ncr_case.plant_id` มาจาก `plant.plant_code` — `schemas.py PLANT_CODE_LENGTH` */
export const PLANT_CODE_MAX_LENGTH = 10;
/** `ncr_case.defect_description` — `models.py DEFECT_DESCRIPTION_LENGTH` */
export const DEFECT_DESCRIPTION_MAX_LENGTH = 255;
/** `ncr_case.investigation_note` — `models.py INVESTIGATION_NOTE_LENGTH` */
export const INVESTIGATION_NOTE_MAX_LENGTH = 255;

/* ------------------------------------------------------------- enum ค่า --- */

export const NCR_REFERENCE_DOC_TYPES = [
  'LOT',
  'GOODS_RECEIPT',
  'OPERATION_CONFIRMATION',
] as const;
export type NcrReferenceDocType = (typeof NCR_REFERENCE_DOC_TYPES)[number];

export const NCR_STATUSES = ['OPEN', 'UNDER_REVIEW', 'CLOSED'] as const;
export type NcrStatus = (typeof NCR_STATUSES)[number];

export const NCR_DISPOSITIONS = [
  'USE_AS_IS',
  'REWORK',
  'SCRAP',
  'RETURN_TO_SUPPLIER',
] as const;
export type NcrDisposition = (typeof NCR_DISPOSITIONS)[number];

/**
 * สิ่งที่ปิดเคสด้วยการตัดสินใจหนึ่งจะทำกับ `lot_master.quality_status` — คัดลอกมา
 * จาก `modules/quality/models.py DISPOSITION_LOT_STATUS` ตรง ๆ (D-108 ข้อ 2)
 *
 * ใช้ **ทำนาย** ผลลัพธ์ก่อนกดยืนยันเท่านั้น (`ncrView.ts` — "THE CONSEQUENCE
 * HEADLINE") เซิร์ฟเวอร์เป็นผู้เขียนค่าจริงเสมอ และตอบกลับมาใน `GET` ครั้งถัดไปของ
 * ล็อตนั้น (ซึ่งหน้าจอนี้ไม่ได้อ่าน — ดูหมายเหตุข้อ 1 ของไฟล์)
 *
 * **เฉพาะ `LOT` เท่านั้นที่มีล็อตให้ย้ายสถานะ** — เคสที่อ้างถึงใบรับเข้าหรือใบยืนยัน
 * งานไม่ย้ายอะไรเลย เพราะทั้งสองไม่ใช่ล็อตเพียงหนึ่งเดียว (`GOODS_RECEIPT` คือการ
 * ส่งของหนึ่งครั้ง และ `OPERATION_CONFIRMATION` อาจผลิตได้หลายล็อต) การเดาว่าจะย้าย
 * ล็อตไหนจะเป็นการเดาที่เปลี่ยนว่าโรงงานใช้วัตถุดิบชิ้นไหนได้
 */
export const DISPOSITION_LOT_STATUS: Readonly<Record<NcrDisposition, string>> = {
  USE_AS_IS: 'RELEASED',
  REWORK: 'BLOCKED',
  SCRAP: 'REJECTED',
  RETURN_TO_SUPPLIER: 'REJECTED',
};

/* ------------------------------------------------------------- กฎธุรกิจ --- */

/** `422` เมื่อ `reference_doc_id` ไม่ตรงกับแถวใดของตารางที่ `reference_doc_type` ระบุ */
export const NCR_REFERENCE_NOT_FOUND = 'NCR_REFERENCE_NOT_FOUND';

/** `422` เมื่อเอกสารที่อ้างถึงมีอยู่จริงแต่เป็นของอีกโรงงานหนึ่ง */
export const NCR_REFERENCE_PLANT_MISMATCH = 'NCR_REFERENCE_PLANT_MISMATCH';

/** `422` เมื่อ `/investigate` ถูกเรียกกับเคสที่ไม่ได้อยู่ที่ `OPEN` (และยังไม่ปิด) */
export const NCR_CASE_NOT_OPEN = 'NCR_CASE_NOT_OPEN';

/**
 * `422` เมื่อ `/investigate` หรือ `/close` ถูกเรียกกับเคสที่ `CLOSED` แล้ว
 *
 * **ห้ามซ่อนปุ่มเมื่อเคสปิดแล้ว** (acceptance ของ WO นี้) — คำตอบนี้เองคือสิ่งที่
 * สอนกติกา "เคสที่ปิดแล้วไม่มีวันถูกเปิดใหม่ ต้องยกเคสใหม่แทน" ให้ผู้ใช้เห็น
 */
export const NCR_CASE_IS_CLOSED = 'NCR_CASE_IS_CLOSED';

/** `400` เมื่อ body พาฟิลด์ที่เซิร์ฟเวอร์เป็นเจ้าของไปด้วย (สถานะ/การตัดสินใจ/ผู้กระทำ) */
export const NCR_CASE_FIELD_IS_SERVER_OWNED = 'NCR_CASE_FIELD_IS_SERVER_OWNED';

/** `403` เมื่อ `plant_code` (หรือโรงงานของเคส) อยู่นอกขอบเขตของผู้เรียก (D-004) */
export const PLANT_OUTSIDE_SCOPE = 'PLANT_OUTSIDE_SCOPE';

/* ------------------------------------------------------------- resource --- */

/**
 * เคสหนึ่งใบ ตาม `NcrCaseResponse` — **ไม่มี `quality_status`** (ดูหมายเหตุข้อ 1
 * ของไฟล์)
 */
export interface NcrCase {
  readonly ncr_id: string;
  readonly plant_id: string;
  readonly plant_code: string;
  readonly reference_doc_type: string;
  readonly reference_doc_id: string;
  readonly status: string;
  readonly defect_description: string;
  readonly raised_by_employee_id: string;
  readonly investigation_note: string | null;
  readonly disposition: string | null;
  readonly decided_by_employee_id: string | null;
  readonly decided_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/** `GET /v1/ncr-cases` — envelope มาตรฐานของทุกกลุ่ม */
export interface NcrCaseListPage {
  readonly items: readonly NcrCase[];
  readonly next_cursor: string | null;
}

/* -------------------------------------------------------------- requests -- */

/**
 * body ของ `POST /v1/ncr-cases` — **ไม่มี `status`/`disposition`**: เคสเริ่มที่
 * `OPEN` เสมอ และการตัดสินใจเกิดที่ `/close` เท่านั้น
 */
export interface CreateNcrCaseBody {
  readonly plant_code: string;
  readonly reference_doc_type: NcrReferenceDocType;
  readonly reference_doc_id: string;
  readonly defect_description: string;
  readonly investigation_note?: string;
}

/** body ของ `POST .../investigate` — `investigation_note` บังคับ (ต่างจากที่อื่น) */
export interface InvestigateNcrCaseBody {
  readonly investigation_note: string;
}

/** body ของ `POST .../close` — `disposition` บังคับ ไม่มี default */
export interface CloseNcrCaseBody {
  readonly disposition: NcrDisposition;
  readonly investigation_note?: string;
}
